import React, { useEffect, useRef, useState } from 'react';
import Logo from './Logo';

/**
 * Branded splash screen shown once per session.
 * Sequence (~1.9s total):
 *   0-100ms   : black backdrop
 *   100-800ms : logo fades in + scales up gently
 *   400-1100ms: tagline "Driven by Quality" fades in
 *   1400ms    : entire splash fades out (500ms)
 *   1900ms    : unmount + callback
 */
const LoadingScreen = ({ onLoadingComplete }) => {
  const [phase, setPhase] = useState(0); // 0 = idle, 1 = visible, 2 = fading out, 3 = done
  const callbackRef = useRef(onLoadingComplete);
  callbackRef.current = onLoadingComplete;

  useEffect(() => {
    // Skip the splash on subsequent navigations within the same tab session.
    if (typeof window !== 'undefined' && sessionStorage.getItem('ad_splash_shown') === '1') {
      setPhase(3);
      if (callbackRef.current) callbackRef.current();
      return () => {};
    }

    const t1 = setTimeout(() => setPhase(1), 60);    // fade in
    const t2 = setTimeout(() => setPhase(2), 1400);  // start fade out
    const t3 = setTimeout(() => {
      setPhase(3);
      try { sessionStorage.setItem('ad_splash_shown', '1'); } catch { /* ignore */ }
      if (callbackRef.current) callbackRef.current();
    }, 1900);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  if (phase >= 3) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-[#0F1E3D] transition-opacity duration-500 ease-out ${
        phase >= 2 ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      data-testid="loading-screen"
    >
      {/* soft radial glow behind the logo */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(14,164,103,0.18) 0%, rgba(15,30,61,0) 55%)',
        }}
      />

      <div className="relative h-full w-full flex items-center justify-center px-6">
        <div className="flex flex-col items-center">
          {/* Logo: fade + scale up */}
          <div
            className={`transition-all duration-700 ease-out ${
              phase >= 1 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-95'
            }`}
          >
            <Logo variant="light" size="lg" />
          </div>

          {/* Tagline: delayed fade in */}
          <p
            className={`mt-6 text-[11px] tracking-[0.35em] uppercase font-semibold text-white/60 transition-opacity duration-700 ease-out ${
              phase >= 1 ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: phase >= 1 ? '350ms' : '0ms' }}
          >
            Driven by Quality
          </p>

          {/* Thin progress line */}
          <div
            className={`mt-8 h-[2px] w-40 bg-white/10 rounded-full overflow-hidden transition-opacity duration-500 ${
              phase >= 1 ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: phase >= 1 ? '500ms' : '0ms' }}
          >
            <div
              className="h-full bg-emerald-500 rounded-full"
              style={{
                animation: 'splashProgress 1200ms ease-out forwards',
              }}
            />
          </div>
        </div>
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes splashProgress {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
